<template>
  <div class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2>{{ modalTitle }}</h2>
        <button @click="$emit('close')" class="btn-close">
          <font-awesome-icon icon="fa-solid fa-xmark" />
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <div class="form-group">
          <label for="client_name">{{ $t('modals.clientName') }} <span class="required">*</span></label>
          <input
            id="client_name"
            v-model="form.vendor_name"
            type="text"
            :placeholder="$t('modals.enterClientName')"
            required
          />
        </div>

        <div class="form-group">
          <label for="client_code">{{ $t('modals.clientCode') }}</label>
          <input
            id="client_code"
            v-model="form.vendor_code"
            type="text"
            :placeholder="$t('modals.enterClientCode')"
          />
          <p class="field-hint">{{ $t('modals.clientCodeHint') }}</p>
        </div>

        <div class="form-group">
          <label for="alias_names">{{ $t('modals.aliasNames') }}</label>
          <div class="alias-names-container">
            <div class="alias-tags">
              <span
                v-for="(alias, index) in form.alias_names"
                :key="index"
                class="alias-tag"
              >
                {{ alias }}
                <button
                  type="button"
                  @click="removeAlias(index)"
                  class="alias-remove"
                >
                  ×
                </button>
              </span>
              <input
                v-model="newAlias"
                @keydown.enter.prevent="addAlias"
                @keydown.,="addAlias"
                type="text"
                class="alias-input"
                :placeholder="$t('modals.addAliasPlaceholder')"
              />
            </div>
            <p class="field-hint">{{ $t('modals.aliasHint') }}</p>
          </div>
        </div>

        <div class="form-group">
          <label for="client_vat">{{ $t('modals.vatNumber') }}</label>
          <input
            id="client_vat"
            v-model="form.vendor_vat"
            type="text"
            placeholder="BG123456789"
          />
        </div>

        <div class="form-group">
          <label for="client_tax_id">{{ $t('modals.taxIdNumber') }}</label>
          <input
            id="client_tax_id"
            v-model="form.vendor_tax_id"
            type="text"
            :placeholder="$t('modals.enterTaxId')"
          />
          <p class="field-hint">{{ $t('modals.taxIdHint') }}</p>
        </div>

        <div class="form-group">
          <label for="client_address">{{ $t('modals.address') }}</label>
          <input
            id="client_address"
            v-model="form.vendor_address"
            type="text"
            :placeholder="$t('modals.enterAddress')"
          />
        </div>

        <div class="form-group form-group-toggle">
          <label class="toggle">
            <input type="checkbox" v-model="form.is_active" />
            <span class="toggle-track"></span>
          </label>
          <span class="toggle-text">{{ $t('modals.active') }}</span>
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-secondary">
            {{ $t('modals.cancel') }}
          </button>
          <button type="submit" class="btn-primary" :disabled="saving">
            {{ saveButtonText }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/utils/api';

const { t } = useI18n();

const props = defineProps({
  client: {
    type: Object,
    default: null
  },
  companyId: {
    type: [String, Number],
    required: true
  }
});

const emit = defineEmits(['close', 'saved']);

const modalTitle = computed(() =>
  props.client ? t('modals.editClient') : t('modals.addClient')
);

const saveButtonText = computed(() =>
  saving.value ? t('modals.saving') : t('modals.saveClient')
);

const form = ref({
  vendor_name: '',
  vendor_code: '',
  alias_names: [],
  vendor_vat: '',
  vendor_tax_id: '',
  vendor_address: '',
  vendor_email: '',
  vendor_phone: '',
  vendor_type: 'client',
  is_active: true
});

const saving = ref(false);
const errorMessage = ref('');
const newAlias = ref('');

watch(() => props.client, (client) => {
  if (client) {
    form.value = {
      ...client,
      alias_names: Array.isArray(client.alias_names) ? client.alias_names : [],
      vendor_tax_id: client.vendor_tax_id || '',
      vendor_vat: client.vendor_vat || '',
      vendor_type: 'client'
    };
  } else {
    form.value = {
      vendor_name: '',
      vendor_code: '',
      alias_names: [],
      vendor_vat: '',
      vendor_tax_id: '',
      vendor_address: '',
      vendor_email: '',
      vendor_phone: '',
      vendor_type: 'client',
      is_active: true
    };
  }
}, { immediate: true });

const addAlias = () => {
  const alias = newAlias.value.trim();
  if (alias && !form.value.alias_names.includes(alias)) {
    form.value.alias_names.push(alias);
    newAlias.value = '';
  }
};

const removeAlias = (index) => {
  form.value.alias_names.splice(index, 1);
};

const handleSubmit = async () => {
  try {
    saving.value = true;
    errorMessage.value = '';

    const payload = { ...form.value, vendor_type: 'client' };

    if (props.client && props.client.id) {
      await api.put(`/api/companies/${props.companyId}/vendors/${props.client.id}`, payload);
    } else {
      await api.post(`/api/companies/${props.companyId}/vendors`, payload);
    }

    emit('saved');
  } catch (error) {
    console.error('Failed to save client:', error);

    if (error.response?.data?.errors?.vendor_code) {
      errorMessage.value = error.response.data.errors.vendor_code[0];
    } else if (error.response?.data?.message) {
      errorMessage.value = error.response.data.message;
    } else {
      errorMessage.value = `Failed to save client: ${error.message}`;
    }
  } finally {
    saving.value = false;
  }
};

const handleOverlayClick = () => {
  emit('close');
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border);
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.btn-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: var(--bg-gray);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.required {
  color: var(--danger);
}

.form-group input[type="text"],
.form-group input[type="email"],
.form-group input[type="tel"] {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.form-group input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-light);
}

/* iOS-style toggle, matches the rest of the app */
.form-group-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;
  margin: 0;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-track {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 20px;
  transition: background 0.2s ease;
  cursor: pointer;
}

.toggle-track::before {
  content: '';
  position: absolute;
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.toggle input:checked + .toggle-track {
  background: var(--primary);
}

.toggle input:checked + .toggle-track::before {
  transform: translateX(16px);
}

.toggle input:focus-visible + .toggle-track {
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.toggle-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  user-select: none;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid var(--border);
}

.btn-secondary,
.btn-primary {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-primary);
}

.btn-secondary:hover {
  background: var(--bg-gray);
}

.btn-primary {
  background: var(--primary);
  border: none;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.alias-names-container {
  width: 100%;
}

.alias-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: white;
  min-height: 42px;
  align-items: center;
}

.alias-tags:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.alias-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 13px;
  color: #1e40af;
  font-weight: 500;
}

.alias-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  color: #3b82f6;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  border-radius: 3px;
  transition: all 0.15s ease;
}

.alias-remove:hover {
  background: #dbeafe;
  color: #1e40af;
}

.alias-input {
  flex: 1;
  min-width: 150px;
  border: none;
  outline: none;
  padding: 4px 6px;
  font-size: 14px;
  color: var(--text-primary);
}

.alias-input::placeholder {
  color: var(--text-light);
}

.field-hint {
  margin: 6px 0 0 0;
  font-size: 12px;
  color: var(--text-secondary);
}

.error-message {
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  color: #c33;
  font-size: 14px;
  line-height: 1.5;
}
</style>
